import type { AxiosRequestConfig } from 'axios';

import { FirebaseService } from '@mfw/firebase/services';
import { captureException } from '@sentry/react';
import axios from 'axios';

import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';
import type { LoginKey, SessionCookieFailureSource } from './sessionCookie';
import type { ApiAuthUser } from '../schemas/users.schema';
import type { UiTenantDto } from './types/tenant.types';

import {
  UnprocessableEntityError,
  ContentTooLargeError,
  InternalServerError,
  UnauthorizedError,
  TooManyRequestsError,
  BadRequestError,
  ForbiddenError,
  NotFoundError,
  ConflictError,
  NetworkError,
  REQUEST_BLOCKED_CODE,
  REQUEST_BLOCKED_MESSAGE,
} from './http';
import { reportNetworkFailure } from './http/reportNetworkFailure';
import { isConnectivityError } from './http/isConnectivityError';
import { isRefusedRenewalError } from './http/isRefusedRenewalError';
import { captureSignOutReasonFromTokenRead } from './http/signOutReason';
import { installSessionCookieInterceptors } from './sessionCookieAxios';
import { getQueryClientLoaderInstance } from '../context';
import { getTenantOverride, resolveActingTenantId } from './tenantOverride';
import { createSessionCookieGuard } from './sessionCookie';
import { describeErrorForTag } from '../utils/describeErrorForTag';
import { queryParamsFromString } from '../utils';
import { HttpStatus } from './types/api.types';
import { queryKeys } from '../queryKeys';
import config from '../config';

/** A decoded JWT payload that is an actual claim set — not `null`, not an array. */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    if (!token) return null;
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    const parsed: unknown = JSON.parse(jsonPayload);
    return isPlainRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * `exp`, in SECONDS, or `null` when the token cannot be decoded or the claim is not a
 * number. Both readers here answer `null` for "unknown", and every caller fails CLOSED on
 * it — which is why the type check is not decoration: a string `exp` coerced to a number
 * would be trusted as a valid deadline.
 *
 * Twilio's token lifecycle is the last consumer of this one. The session cookie has no
 * deadline this side can read — it is HttpOnly and the backend is the authority — so the
 * guard stopped asking; the ID token's hour is no longer anything the app counts down.
 */
export function decodeJwtExp(token: string) {
  const exp = decodeJwt(token)?.exp;
  return typeof exp === 'number' ? exp : null;
}

/**
 * The claims that identify ONE sign-in of one account: `sub`, the identity tenant and
 * `auth_time` (the sign-in instant, unchanged by every hourly refresh). Used only to tell
 * a rollover from a new login; never trusted as proof, the backend is the authority.
 */
export function decodeJwtLoginKey(token: string): LoginKey | null {
  const claims = decodeJwt(token);
  if (!claims) return null;

  const { sub: subject, auth_time: authTime, firebase } = claims;
  const identityTenantId = isPlainRecord(firebase)
    ? firebase.tenant
    : undefined;

  if (typeof subject !== 'string' || subject === '') return null;
  if (typeof identityTenantId !== 'string' || identityTenantId === '') {
    return null;
  }
  if (
    typeof authTime !== 'number' ||
    !Number.isSafeInteger(authTime) ||
    authTime < 0
  ) {
    return null;
  }

  return { authTime, identityTenantId, subject };
}

// Deployed, the API is on the page's own origin: the load balancer routes /api to the
// backend. This is required because of cookies and cross site tracking prevention on browsers.
// Exported because non-axios consumers need the same base — the <link rel="manifest">
// in ManifestProvider must be same-origin for PWA installability.
export const apiBaseUrl = config.environment.isLocal
  ? `${config.cloud.defaults.apiBaseUrl}/api`
  : '/api';

/**
 * The same rule, for the one consumer that cannot use a relative base: the Socket.io
 * handshake needs an absolute origin, so `/api` is not an answer for it.
 *
 * It has to resolve to the host `apiBaseUrl` above resolves to, and that is the whole
 * point of this constant (auth-11 P2-1). The socket authenticates with the `auth` cookie
 * and nothing else — `auth-cookie.ts` sets no `Domain`, so the browser stores it for the
 * exact host that set it and sends it nowhere else. Deployed, that host is the page's
 * own, because the `Set-Cookie` came back on its `/api`; connecting to any other host
 * sends a handshake with no credential in it, and the gateway disconnects it. So:
 * the page's own origin, where the load balancer routes `/api/socket.io/` (WebSocket
 * upgrade included) to the same backend as `/api/`.
 */
export const socketBaseUrl = config.environment.isLocal
  ? config.cloud.defaults.apiBaseUrl
  : window.location.origin;

const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
});

axiosInstance.defaults.withCredentials = true;

// Active app language — sent as Accept-Language so translated BE responses
// (error messages) follow the app language instead of the browser default.
// Synced from TranslationsProvider via ApiClientProvider.
let apiLocale: null | string = null;
export function setApiLocale(locale: string) {
  apiLocale = locale;
}

// ── The acting tenant, resolved in exactly one place ────────────────────────────────
//
// Through `resolveActingTenantId`, not a second copy of the rule: this is what every
// branding route resolves its target from, and `/admin/rebrand` seeds its editors from
// the SAME function. Two hand-written copies could drift, and the failure mode of that
// drift is a page that shows one tenant's palette while saving into another's.
function resolveActingTenant() {
  const queryClient = getQueryClientLoaderInstance();
  const tenant = queryClient?.getQueryData<UiTenantDto>(
    queryKeys.tenants.byDomain({
      domain: config.cloud.defaults.tenantDomain,
    }).queryKey,
  );
  // super-admin tenant switcher: a stored override wins over the domain tenant
  // (BE rejects cross-tenant use for anyone but platform_super_admin)
  return resolveActingTenantId(getTenantOverride(), tenant?.id);
}

/**
 * The same rule, normalised to a plain string so the guard can compare two contexts for
 * equality. `''` means "no tenant resolved yet" — it is a comparison key, never a header
 * value, because sending an empty `X-Tenant-Id` is not the same as omitting it.
 */
function resolveSessionContextKey() {
  return resolveActingTenant() ?? '';
}

// ── Header interceptor ──────────────────────────────────────────────────────────────
//
// Registered BEFORE the session interceptors on purpose. Axios runs request interceptors
// last-registered-first, so the session barrier below runs first and this one then sees
// the acting tenant it captured — the renewal and the request it unblocked cannot end up
// addressed to two different tenants.
axiosInstance.interceptors.request.use((request) => {
  // The captured key is the acting tenant the barrier RELEASED this request under, and the
  // guard judges the answer against exactly that. `undefined` means the barrier never ran —
  // an anonymous request, the public site, the login screen — and only there may the tenant
  // be resolved here. `''` means the barrier ran while the domain tenant was still loading:
  // absent then, absent on the wire. Re-resolving it is how a request and its own stamp end
  // up naming two different tenants, and the guard then rejects the response it asked for.
  request.headers.set(
    'X-Tenant-Id',
    request.sessionContextKey === undefined
      ? resolveActingTenant()
      : request.sessionContextKey || undefined,
  );
  if (apiLocale) {
    request.headers.set('Accept-Language', apiLocale);
  }
  // Q2 (global-timezone spec): /auth/me records this as the account's DETECTED zone
  // (`UserSettings.detectedTimezone`), never as its explicit choice — so a wrong guess
  // self-heals on the next login while a real choice survives. It is what the whole
  // scheduling/stats stack resolves in for accounts that never opened the Personal
  // Details timezone picker.
  request.headers.set(
    'X-Timezone',
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  // Stamped HERE, in the last interceptor to run, so the duration a dropped request is
  // reported with measures the wire and not the session barrier that held it: a request
  // parked behind a renewal has waited, and that wait says nothing about the connection.
  request.transportSentAt = Date.now();
  return request;
});

/**
 * Set by the app so a dead session can say so on screen. Module-level for the same reason
 * `apiLocale` is: the guard lives here, outside React, and this file is what the guard talks
 * to. `null` while nothing is mounted to hear it.
 */
let onSessionUnrenewable: (() => void) | null = null;
export function setSessionUnrenewableHandler(handler: (() => void) | null) {
  onSessionUnrenewable = handler;
}

/**
 * An unrenewable session is otherwise completely silent: a barrier rejection is not an
 * `AxiosError`, so it is not a `BaseApiResponseError`, so the toast predicate drops it —
 * and the bounded fix deliberately does not sign anybody out. Without this the page just
 * stops filling in.
 *
 * Deliberately coarse. No original error, no JWT, no Authorization header, no request
 * config, no decoded claims: this is a health signal, not a debugging dump.
 *
 * Both halves — the Sentry event and the on-screen message — ride on ONE call because the
 * guard makes exactly one per unhealthy stretch, from the first hard failure until a
 * renewal succeeds again. A wake-up burst has ten rejected waiters and one dead session; a
 * ten-minute outage has twenty expired cooldowns and one dead session. Neither the pacing
 * nor the swallowing of a throwing handler is repeated here: the guard already does both.
 *
 * MYFITWORLD-V2-6R: the two halves part company for ONE kind of failure. A refused IDENTITY
 * is not an unrenewable session — it is a session the app ends on purpose, and `AuthProvider`
 * signs out on the very same predicate a moment later. So:
 *
 *   * the Sentry event stays, demoted to `warning` — one of these is the design working, a
 *     hundred at once would still be worth seeing;
 *   * the on-screen message goes. It says the session could not be renewed, which is not
 *     what happened, and it says it on top of the login screen that has already said the
 *     true thing more plainly. That message belongs to the case this is not: a session that
 *     is over for a reason no screen explains.
 *
 * `isRefusedRenewalError` is the predicate `AuthProvider` signs out on, not a second list —
 * sen-3 M1 was two layers answering differently about one code.
 */
function reportHardSessionFailure(
  source: SessionCookieFailureSource,
  error: unknown,
) {
  const refusedIdentity = isRefusedRenewalError(error);
  captureException(new Error(`Session cookie renewal failed (${source})`), {
    level: refusedIdentity ? 'warning' : 'error',
    tags: {
      sessionRenewalError: describeErrorForTag(error),
      sessionRenewalSource: source,
    },
  });
  if (refusedIdentity) return;
  try {
    onSessionUnrenewable?.();
  } catch {
    // nothing to do: this is a notification, not the outcome
  }
}

/**
 * Owns the lifetime of the `auth` cookie. `renewCookie` goes through this module's own
 * `get()` helper rather than the raw instance, and that is load-bearing: `get()` is what
 * runs `handleErrors`, and `handleErrors` is what turns a 401 into `UnauthorizedError` —
 * the only error `AuthProvider` signs a user out on.
 */
export const sessionCookie = createSessionCookieGuard<
  BaseApiResponse<ApiAuthUser> | undefined
>({
  decodeLoginKey: decodeJwtLoginKey,
  getContextKey: resolveSessionContextKey,
  // Wrapped, because this is the app's most-used session token read AND the last moment
  // at which a refused one is still recognisable: on `auth/user-token-expired` the SDK
  // signs the user out from inside this call and only then rejects, so by the time the
  // guard has turned the rejection into a renewal failure the provider's no-user branch
  // has already navigated to the login screen the refusal would have explained. The
  // wrapper classifies around the read and rethrows the error untouched — everything
  // below still sees exactly what Firebase threw.
  getIdToken: async (forceRefresh) =>
    captureSignOutReasonFromTokenRead(async () =>
      FirebaseService.getInstance()
        .getFirebaseAuth()
        ?.currentUser?.getIdToken(forceRefresh),
    ),
  // A renewal talks to TWO hosts, and the connection can be gone at either end.
  //
  // Google, via `getIdToken`: the ONE Firebase code that describes the connection rather
  // than the session. Every other one — `auth/user-token-expired`, `auth/user-disabled` —
  // is a session that really is over, and stays news.
  //
  // Us, via `renewCookie`: `NetworkError`, the request that went out and got no answer.
  // Sentry 6Q is exactly this half — an iPhone whose tab went to the background mid-boot —
  // and it was missing, so the guard called an aborted XHR an unrenewable session.
  //
  // The pair lives in `isConnectivityError`, shared with `AuthProvider`'s
  // `shouldRetryDroppedRenewal` — sen-3 M1 was these two answering differently about the
  // same failure.
  isConnectivityFault: isConnectivityError,
  now: () => Date.now(),
  renewCookie: (token, contextKey) =>
    get<BaseApiResponse<ApiAuthUser>>('/v2/auth/me', {
      _retry: true,
      headers: { authorization: `Bearer ${token}` },
      sessionContextKey: contextKey,
      skipSessionRenewal: true,
    }),
  reportHardFailure: reportHardSessionFailure,
});

installSessionCookieInterceptors(axiosInstance, sessionCookie);

// A JSON Blob was already parsed by `normalizeErrorBody`; a Blob still here is not JSON.
const isApiEnvelope = (data: unknown) =>
  typeof data === 'object' && data !== null && !(data instanceof Blob);

const handleErrors = <TErrors extends Record<string, any>>(e: unknown) => {
  if (axios.isAxiosError(e)) {
    if (e.response) {
      const errors = e.response.data.errors;
      const status = e.response.status;
      const message = e.response?.data?.message;
      // machine-readable BE error code (e.g. NO_SAVED_CARD) — flows branch on
      // it instead of string-matching the translated message
      const code = e.response?.data?.code;
      // Task 5 — MEMBERSHIP_VISITS_USED_UP's identity, absent on every other
      // BadRequest body.
      const userMembershipId = e.response?.data?.userMembershipId;
      const errorEndDate = e.response?.data?.endDate;
      const existingCheckIn = e.response?.data?.existingCheckIn;
      // Locker keys — HOLDER_HAS_KEY / KEY_ALREADY_DISABLED, absent elsewhere.
      const currentAssignment = e.response?.data?.currentAssignment;
      const disabledNumberId = e.response?.data?.disabledNumberId;
      switch (status) {
        case HttpStatus.BadRequest:
          throw new BadRequestError<TErrors>({
            code,
            endDate: errorEndDate,
            existingCheckIn,
            message,
            userMembershipId,
          });
        case HttpStatus.Unauthorized:
          throw new UnauthorizedError({ message });
        case HttpStatus.Forbidden:
          // Not the API's envelope: the edge (Cloud Armor) refused the request before the
          // backend saw it, and its HTML page carries no sentence for the toast to show.
          if (!isApiEnvelope(e.response.data)) {
            throw new ForbiddenError<TErrors>({
              code: REQUEST_BLOCKED_CODE,
              message: REQUEST_BLOCKED_MESSAGE,
            });
          }
          // `code` forwarded — `ORG_BILLING_LOCKED` arrives on a 403, and it is
          // the only field a flow can branch on to pick translated copy over the
          // backend's English sentence.
          throw new ForbiddenError<TErrors>({ code, message });
        case HttpStatus.NotFound:
          throw new NotFoundError<TErrors>({ code, message });
        case HttpStatus.Conflict:
          // F8 — machine-readable code (e.g. LAST_OWNER) must survive to the
          // FE flow, same as BadRequestError above; was dropped before.
          throw new ConflictError<TErrors>({
            code,
            currentAssignment,
            disabledNumberId,
            message,
          });
        case HttpStatus.ContentTooLarge:
          throw new ContentTooLargeError<TErrors>({ code, message });
        case HttpStatus.UnprocessableEntity:
          // `code` forwarded — a coded business refusal (locker keys'
          // NUMBER_OUT_OF_RANGE) arrives on a 422 next to Zod's uncoded
          // field-error envelope, and only the code tells them apart.
          throw new UnprocessableEntityError<TErrors>({
            code,
            errors,
            message,
          });
        case HttpStatus.TooManyRequests:
          // A throttle refusal is "too many attempts", not a server fault — it used to
          // fall through to `default` and read as a 500. The backend's sentence is the
          // library's English, so the error carries a translatable key instead.
          throw new TooManyRequestsError<TErrors>();
        case HttpStatus.InternalServerError:
          throw new InternalServerError<TErrors>({ message });
        default:
          throw new InternalServerError({ message });
      }
    } else if (e.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      //
      // NOT an `InternalServerError`: that is a claim about the server, and the server may
      // well have answered — Sentry 6Q is a request iOS killed when the tab was hidden,
      // which the backend served with 200 a moment later. Only this branch knows the
      // difference, so it is the one place that can name it.
      //
      // And the one place that can REPORT it. The toast went quiet for background reads,
      // which is right — the sentence names nothing the user can act on — but quiet and
      // unrecorded are not the same thing: the config is still here, so the event can say
      // which request it was, while the error thrown below no longer can.
      reportNetworkFailure(e);
      throw new NetworkError({
        message: 'Error, no response from server',
      });
    } else {
      // Something happened in setting up the request that triggered an Errorw
      reportNetworkFailure(e);
      throw new InternalServerError({
        message: 'Error, no response from server',
      });
    }
  }
  throw e;
};

export async function get<TResponseData>(
  url: string,
  config?: {
    query?: Record<string, string | string[] | undefined>;
  } & AxiosRequestConfig<TResponseData>,
) {
  try {
    const queryParams = queryParamsFromString(config?.query || {});
    if (queryParams) {
      url = `${url}?${queryParams}`;
    }
    const response = await axiosInstance.get<TResponseData>(url, config);
    return response.data;
  } catch (e) {
    handleErrors(e);
  }
}

export async function post<TResponseData, TArgsData = any>(
  url: string,
  data?: TArgsData,
  config?: AxiosRequestConfig<TResponseData>,
) {
  try {
    const response = await axiosInstance.post<BaseApiResponse<TResponseData>>(
      url,
      data,
      config,
    );
    return response.data;
  } catch (e) {
    handleErrors(e);
  }
}

export async function postWithPagination<TResponseData, TArgsData = any>(
  url: string,
  data?: TArgsData,
  config?: AxiosRequestConfig<TResponseData>,
) {
  try {
    const response = await axiosInstance.post<
      BaseApiPaginatedResponse<TResponseData>
    >(url, data, config);
    return response.data;
  } catch (e) {
    handleErrors(e);
  }
}

export async function put<TResponseData, TArgsData = any>(
  url: string,
  data?: TArgsData,
  config?: AxiosRequestConfig<TResponseData>,
) {
  try {
    const response = await axiosInstance.put<BaseApiResponse<TResponseData>>(
      url,
      data,
      config,
    );
    return response.data;
  } catch (e) {
    handleErrors(e);
  }
}

export async function destroy(url: string, config?: AxiosRequestConfig<void>) {
  try {
    await axiosInstance.delete(url, config);
  } catch (e) {
    handleErrors(e);
  }
}

export async function patch<TResponseData, TArgsData = any>(
  url: string,
  data?: TArgsData,
  config?: AxiosRequestConfig<TResponseData>,
) {
  try {
    const response = await axiosInstance.patch<BaseApiResponse<TResponseData>>(
      url,
      data,
      config,
    );
    return response.data;
  } catch (e) {
    handleErrors(e);
  }
}
